import { useCallback, useMemo, useState } from "react";
import { polygon } from "viem/chains";
import { usePublicClient, useSwitchChain } from "wagmi";
import { HyperliquidAdapter } from "@hyperhedge/hyperliquid";
import {
  createPolymarketAdapter,
  ensureBuyAllowances,
  type PolymarketAdapter,
} from "@hyperhedge/polymarket";
import type { PerpLeg, PredictionLeg } from "../lib/composePayoff";
import type { SelectedPerp, SelectedPred } from "../types";
import { useTradingSigner } from "./use-trading-signer";

export type ExecutionStage =
  | "idle"
  | "deriving-poly-creds"
  | "checking-allowance"
  | "switching-chain"
  | "approving-usdc"
  | "preparing-hl"
  | "approving-hl-agent"
  | "syncing-leverage"
  | "placing-hl"
  | "signing-poly"
  | "success"
  | "error";

export type ExecutionState = {
  stage: ExecutionStage;
  /** Human-readable message describing what's happening / what failed. */
  message?: string;
  hlOrderId?: number;
  polyOrderId?: string;
  error?: string;
};

const DEFAULT_MAX_SLIPPAGE = 0.01; // 1%

function stageLabel(s: ExecutionStage): string {
  switch (s) {
    case "deriving-poly-creds":
      return "Sign Polymarket auth (1× per session)…";
    case "checking-allowance":
      return "Checking USDC.e allowance…";
    case "switching-chain":
      return "Switch wallet to Polygon…";
    case "approving-usdc":
      return "Approve USDC.e for CTF Exchange…";
    case "preparing-hl":
      return "Preparing Hyperliquid session…";
    case "approving-hl-agent":
      return "Sign Hyperliquid agent approval…";
    case "syncing-leverage":
      return "Syncing leverage to Hyperliquid…";
    case "placing-hl":
      return "Placing Hyperliquid market order…";
    case "signing-poly":
      return "Sign Polymarket order…";
    default:
      return "";
  }
}

export function useCompositeExecution() {
  const signer = useTradingSigner();
  const polygonClient = usePublicClient({ chainId: polygon.id });
  const { switchChainAsync } = useSwitchChain();
  const [state, setState] = useState<ExecutionState>({ stage: "idle" });

  /** Stable adapter pair — rebuilt only when the signer changes. */
  const adapters = useMemo(() => {
    if (!signer) return null;
    const hl = new HyperliquidAdapter({
      walletClient: signer.walletClient,
      authMode: "vsecure",
    });
    const poly: PolymarketAdapter = createPolymarketAdapter({
      gammaUrl: `${window.location.origin}/api/polymarket`,
      clobUrl: `${window.location.origin}/api/polymarket`,
      signer: signer.polymarketSigner,
    });
    return { hl, poly };
  }, [signer]);

  const execute = useCallback(
    async (input: {
      perp: PerpLeg;
      pred: PredictionLeg;
      perpMarket: SelectedPerp;
      predMarket: SelectedPred;
    }) => {
      if (!signer || !adapters) {
        setState({
          stage: "error",
          error: "Wallet not connected — log in first.",
        });
        return;
      }

      try {
        // ───── 1. Polymarket: derive API key trio (cached in sessionStorage) ─────
        setState({
          stage: "deriving-poly-creds",
          message: stageLabel("deriving-poly-creds"),
        });
        await adapters.poly.ensureCredentials();

        // ───── 2. USDC.e allowance pre-check on Polygon ─────
        setState({
          stage: "checking-allowance",
          message: stageLabel("checking-allowance"),
        });
        if (!polygonClient) {
          throw new Error(
            "Polygon RPC client unavailable — refresh and try again.",
          );
        }
        // BUY cost in USDC.e base units (6 decimals), with a small buffer.
        const requiredUsdc = BigInt(
          Math.ceil(
            input.pred.shares * input.pred.entryCents * 1e4 * 1.05,
          ), // shares * (cents/100) * 1e6 base units * 5% buffer
        );
        const allowanceResult = await ensureBuyAllowances(
          polygonClient,
          signer.walletClient,
          signer.address,
          input.predMarket.negRisk,
          requiredUsdc,
        );
        // If we have any pending spenders, that means we needed to switch chain
        // first (the wallet must be on Polygon to send approve txs).
        if (allowanceResult.pending.length > 0) {
          // Re-run with chain switch; the helper above attempts to send the
          // approval directly, which may fail silently if wallet is on the
          // wrong chain. Force a switch then retry once.
          setState({
            stage: "switching-chain",
            message: stageLabel("switching-chain"),
          });
          try {
            await switchChainAsync({ chainId: polygon.id });
          } catch (e) {
            throw new Error(
              `Need to be on Polygon to approve USDC.e — ${(e as Error).message}`,
            );
          }
          setState({
            stage: "approving-usdc",
            message: stageLabel("approving-usdc"),
          });
          const retry = await ensureBuyAllowances(
            polygonClient,
            signer.walletClient,
            signer.address,
            input.predMarket.negRisk,
            requiredUsdc,
          );
          if (retry.pending.length > 0 && retry.txHashes.length === 0) {
            throw new Error(
              "USDC.e approval did not complete — please retry.",
            );
          }
        }

        // ───── 3. Hyperliquid: ensure agent wallet exists & is approved ─────
        setState({ stage: "preparing-hl", message: stageLabel("preparing-hl") });
        await adapters.hl.initialize();
        const step = await adapters.hl.trading.initWallet(signer.address);
        if (step === "approve_agent") {
          setState({
            stage: "approving-hl-agent",
            message: stageLabel("approving-hl-agent"),
          });
          const approved = await adapters.hl.trading.approveAgent(
            async (params) => {
              const sig = await (
                signer.walletClient.signTypedData as (
                  args: Record<string, unknown>,
                ) => Promise<`0x${string}`>
              )({
                account: signer.address,
                domain: params.domain,
                types: params.types,
                primaryType: params.primaryType,
                message: params.message,
              });
              return sig;
            },
            signer.address,
          );
          if (!approved) throw new Error("Hyperliquid agent approval failed.");
        } else if (step === "generate_wallet" || step === "disconnected") {
          throw new Error(
            `Hyperliquid setup incomplete (${step}). Try reconnecting your wallet.`,
          );
        }

        // ───── 4. Sync leverage to the simulated value (cross margin) ─────
        setState({
          stage: "syncing-leverage",
          message: stageLabel("syncing-leverage"),
        });
        const lev = Math.max(1, Math.floor(input.perp.leverage));
        const levResult = await adapters.hl.trading.updateLeverage({
          symbol: input.perpMarket.coin,
          leverage: lev,
          isCross: true,
        });
        if (!levResult.success) {
          // Non-fatal — order may still go through with the user's existing
          // leverage. Surface as error only if it's clearly the cause.
          // We keep going and hope for the best; HL ignores no-op updates.
        }

        // ───── 5. Place HL market order ─────
        setState({ stage: "placing-hl", message: stageLabel("placing-hl") });
        const hlResult = await adapters.hl.trading.placeMarketOrder({
          symbol: input.perpMarket.coin,
          side: input.perp.side === "long" ? "buy" : "sell",
          size: String(input.perp.size),
          maxSlippage: DEFAULT_MAX_SLIPPAGE,
        });
        if (!hlResult.success) {
          throw new Error(hlResult.error ?? "Hyperliquid order rejected.");
        }

        // ───── 6. Place Polymarket order (FOK — fill-now or cancel) ─────
        setState({
          stage: "signing-poly",
          message: stageLabel("signing-poly"),
          hlOrderId: hlResult.orderId,
        });
        const yesTokenId = input.predMarket.yesTokenId;
        const tokenId =
          input.pred.outcome === "yes"
            ? yesTokenId
            : input.predMarket.clobTokenIds.find((t) => t !== yesTokenId);
        if (!tokenId) {
          throw new Error("Polymarket: token id not found for selected outcome.");
        }

        const conditionId = input.predMarket.conditionId ?? input.predMarket.id;
        const polyResult = await adapters.poly.trading.placeOrder({
          marketId: `polymarket:${conditionId}`,
          tokenId,
          venue: "polymarket",
          side: "buy",
          type: "market",
          price: (input.pred.entryCents / 100).toFixed(3),
          size: String(input.pred.shares),
          tif: "fok",
        });
        if (!polyResult.success) {
          throw new Error(polyResult.error ?? "Polymarket order rejected.");
        }

        setState({
          stage: "success",
          hlOrderId: hlResult.orderId,
          polyOrderId: polyResult.orderId,
        });
      } catch (e) {
        setState({
          stage: "error",
          error: (e as Error).message ?? String(e),
        });
      }
    },
    [signer, adapters, polygonClient, switchChainAsync],
  );

  const reset = useCallback(() => setState({ stage: "idle" }), []);

  return {
    state,
    execute,
    reset,
    ready: !!signer,
    address: signer?.address ?? null,
  };
}
